package diana.soleil.design.iraniancanadienbazzar.core.firebase

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.remoteconfig.remoteConfig
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Typed wrapper over GitLive Remote Config. Exposes the current [RemoteConfigValues] as a [StateFlow]
 * (seeded with [RemoteConfigValues.Defaults]); call [refresh] at app start (and optionally on resume)
 * to fetch + activate. Feature flags and city rollout read from [values]; never read raw keys in UI.
 */
interface RemoteConfigService {
    val values: StateFlow<RemoteConfigValues>
    suspend fun refresh(): DataResult<RemoteConfigValues>
}

internal class DefaultRemoteConfigService(
    // Debug builds fetch fresh config on every refresh (QA sees flag flips immediately); release
    // builds keep a sane throttle so fleets don't hammer the RC backend.
    private val isDebug: Boolean,
) : RemoteConfigService {

    // Lazy so merely resolving this singleton (e.g. in a preview where Firebase isn't initialized)
    // doesn't throw; the access happens inside refresh()'s try/catch.
    private val remoteConfig by lazy { Firebase.remoteConfig }
    private val _values = MutableStateFlow(RemoteConfigValues.Defaults)
    override val values: StateFlow<RemoteConfigValues> = _values.asStateFlow()

    override suspend fun refresh(): DataResult<RemoteConfigValues> = try {
        remoteConfig.settings {
            minimumFetchInterval = if (isDebug) kotlin.time.Duration.ZERO else kotlin.time.Duration.parse("1h")
        }
        remoteConfig.setDefaults(
            RemoteConfigKeys.MIN_SUPPORTED_APP_VERSION to RemoteConfigValues.Defaults.minSupportedAppVersion,
            RemoteConfigKeys.FEATURE_CHAT to RemoteConfigValues.Defaults.chatEnabled,
            RemoteConfigKeys.FEATURE_FEED to RemoteConfigValues.Defaults.feedEnabled,
            RemoteConfigKeys.FEATURE_MARKETPLACE to RemoteConfigValues.Defaults.marketplaceEnabled,
            RemoteConfigKeys.FEATURE_ORDERING to RemoteConfigValues.Defaults.orderingEnabled,
            RemoteConfigKeys.FEATURE_EVENTS to RemoteConfigValues.Defaults.eventsEnabled,
            RemoteConfigKeys.FEATURE_HIDDEN_GEMS to RemoteConfigValues.Defaults.hiddenGemsEnabled,
            RemoteConfigKeys.FEATURE_DANESTANIHA to RemoteConfigValues.Defaults.danestanihaEnabled,
            RemoteConfigKeys.FEATURE_SHAHNAMEH to RemoteConfigValues.Defaults.shahnamehEnabled,
            RemoteConfigKeys.FEATURE_POETRY to RemoteConfigValues.Defaults.poetryEnabled,
            RemoteConfigKeys.FEATURE_STORIES to RemoteConfigValues.Defaults.storiesEnabled,
            RemoteConfigKeys.FEATURE_FAL to RemoteConfigValues.Defaults.falEnabled,
            RemoteConfigKeys.FEATURE_DEALS to RemoteConfigValues.Defaults.dealsEnabled,
            RemoteConfigKeys.FEATURE_PEOPLE to RemoteConfigValues.Defaults.peopleEnabled,
            RemoteConfigKeys.FEATURE_PAYMENTS to RemoteConfigValues.Defaults.paymentsEnabled,
            RemoteConfigKeys.HOME_DENSE_SECTIONS to RemoteConfigValues.Defaults.homeDenseSectionsEnabled,
            RemoteConfigKeys.GEMS_ADULT_JOKES to RemoteConfigValues.Defaults.gemsAdultJokesEnabled,
            RemoteConfigKeys.SPONSORED_SLOTS_MAX to RemoteConfigValues.Defaults.sponsoredSlotsMax.toString(),
            RemoteConfigKeys.ACTIVE_LAUNCH_CITIES to RemoteConfigValues.Defaults.activeLaunchMetros.joinToString(","),
            RemoteConfigKeys.SHARE_BASE_URL to RemoteConfigValues.Defaults.shareBaseUrl,
            RemoteConfigKeys.SUNSET_MODE_ENABLED to RemoteConfigValues.Defaults.sunsetModeEnabled,
            RemoteConfigKeys.SUNSET_MESSAGE_EN to RemoteConfigValues.Defaults.sunsetMessageEn,
            RemoteConfigKeys.SUNSET_MESSAGE_FR to RemoteConfigValues.Defaults.sunsetMessageFr,
            RemoteConfigKeys.SUNSET_MESSAGE_FA to RemoteConfigValues.Defaults.sunsetMessageFa,
            RemoteConfigKeys.MAINTENANCE_MODE_ENABLED to RemoteConfigValues.Defaults.maintenanceModeEnabled,
            RemoteConfigKeys.MAINTENANCE_MESSAGE_EN to RemoteConfigValues.Defaults.maintenanceMessageEn,
            RemoteConfigKeys.MAINTENANCE_MESSAGE_FR to RemoteConfigValues.Defaults.maintenanceMessageFr,
            RemoteConfigKeys.MAINTENANCE_MESSAGE_FA to RemoteConfigValues.Defaults.maintenanceMessageFa,
            RemoteConfigKeys.FORCE_UPDATE_URL to RemoteConfigValues.Defaults.forceUpdateUrl,
        )
        remoteConfig.fetchAndActivate()
        val snapshot = RemoteConfigValues(
            minSupportedAppVersion = string(RemoteConfigKeys.MIN_SUPPORTED_APP_VERSION, RemoteConfigValues.Defaults.minSupportedAppVersion),
            chatEnabled = boolean(RemoteConfigKeys.FEATURE_CHAT, RemoteConfigValues.Defaults.chatEnabled),
            feedEnabled = boolean(RemoteConfigKeys.FEATURE_FEED, RemoteConfigValues.Defaults.feedEnabled),
            marketplaceEnabled = boolean(RemoteConfigKeys.FEATURE_MARKETPLACE, RemoteConfigValues.Defaults.marketplaceEnabled),
            orderingEnabled = boolean(RemoteConfigKeys.FEATURE_ORDERING, RemoteConfigValues.Defaults.orderingEnabled),
            eventsEnabled = boolean(RemoteConfigKeys.FEATURE_EVENTS, RemoteConfigValues.Defaults.eventsEnabled),
            hiddenGemsEnabled = boolean(RemoteConfigKeys.FEATURE_HIDDEN_GEMS, RemoteConfigValues.Defaults.hiddenGemsEnabled),
            danestanihaEnabled = boolean(RemoteConfigKeys.FEATURE_DANESTANIHA, RemoteConfigValues.Defaults.danestanihaEnabled),
            shahnamehEnabled = boolean(RemoteConfigKeys.FEATURE_SHAHNAMEH, RemoteConfigValues.Defaults.shahnamehEnabled),
            poetryEnabled = boolean(RemoteConfigKeys.FEATURE_POETRY, RemoteConfigValues.Defaults.poetryEnabled),
            storiesEnabled = boolean(RemoteConfigKeys.FEATURE_STORIES, RemoteConfigValues.Defaults.storiesEnabled),
            falEnabled = boolean(RemoteConfigKeys.FEATURE_FAL, RemoteConfigValues.Defaults.falEnabled),
            dealsEnabled = boolean(RemoteConfigKeys.FEATURE_DEALS, RemoteConfigValues.Defaults.dealsEnabled),
            peopleEnabled = boolean(RemoteConfigKeys.FEATURE_PEOPLE, RemoteConfigValues.Defaults.peopleEnabled),
            paymentsEnabled = boolean(RemoteConfigKeys.FEATURE_PAYMENTS, RemoteConfigValues.Defaults.paymentsEnabled),
            homeDenseSectionsEnabled = boolean(RemoteConfigKeys.HOME_DENSE_SECTIONS, RemoteConfigValues.Defaults.homeDenseSectionsEnabled),
            gemsAdultJokesEnabled = boolean(RemoteConfigKeys.GEMS_ADULT_JOKES, RemoteConfigValues.Defaults.gemsAdultJokesEnabled),
            sponsoredSlotsMax = long(RemoteConfigKeys.SPONSORED_SLOTS_MAX, RemoteConfigValues.Defaults.sponsoredSlotsMax.toLong()).toInt(),
            activeLaunchMetros = parseMetros(string(RemoteConfigKeys.ACTIVE_LAUNCH_CITIES, "")).ifEmpty {
                RemoteConfigValues.Defaults.activeLaunchMetros
            },
            shareBaseUrl = string(RemoteConfigKeys.SHARE_BASE_URL, RemoteConfigValues.Defaults.shareBaseUrl),
            sunsetModeEnabled = boolean(RemoteConfigKeys.SUNSET_MODE_ENABLED, RemoteConfigValues.Defaults.sunsetModeEnabled),
            sunsetMessageEn = string(RemoteConfigKeys.SUNSET_MESSAGE_EN, RemoteConfigValues.Defaults.sunsetMessageEn),
            sunsetMessageFr = string(RemoteConfigKeys.SUNSET_MESSAGE_FR, RemoteConfigValues.Defaults.sunsetMessageFr),
            sunsetMessageFa = string(RemoteConfigKeys.SUNSET_MESSAGE_FA, RemoteConfigValues.Defaults.sunsetMessageFa),
            maintenanceModeEnabled = boolean(RemoteConfigKeys.MAINTENANCE_MODE_ENABLED, RemoteConfigValues.Defaults.maintenanceModeEnabled),
            maintenanceMessageEn = string(RemoteConfigKeys.MAINTENANCE_MESSAGE_EN, RemoteConfigValues.Defaults.maintenanceMessageEn),
            maintenanceMessageFr = string(RemoteConfigKeys.MAINTENANCE_MESSAGE_FR, RemoteConfigValues.Defaults.maintenanceMessageFr),
            maintenanceMessageFa = string(RemoteConfigKeys.MAINTENANCE_MESSAGE_FA, RemoteConfigValues.Defaults.maintenanceMessageFa),
            forceUpdateUrl = string(RemoteConfigKeys.FORCE_UPDATE_URL, RemoteConfigValues.Defaults.forceUpdateUrl),
        )
        _values.value = snapshot
        DataResult.Success(snapshot)
    } catch (t: Throwable) {
        // Keep last-good/defaults in [values]; surface the failure for logging/telemetry.
        DataResult.Failure(AppError.Network(t))
    }

    private fun string(key: String, fallback: String): String =
        remoteConfig.getValue(key).asString().ifEmpty { fallback }

    private fun boolean(key: String, fallback: Boolean): Boolean =
        runCatching { remoteConfig.getValue(key).asBoolean() }.getOrDefault(fallback)

    private fun long(key: String, fallback: Long): Long =
        runCatching { remoteConfig.getValue(key).asLong() }.getOrDefault(fallback)
}
